import React, { useState, useEffect, useCallback } from "react";
import { merchantApi } from "../../redux/api/apiConfig";
import "rsuite/dist/styles/rsuite-default.css";
import { toast } from "react-toastify";
import * as API from "../../ApiUrl";
import usePageTitle from "../../hooks/usePageTitle";
import { Box, Stack, TextField, MenuItem, InputAdornment, Divider } from "@mui/material";
import { KycSubmittedTable } from "./components";
import { handleApiErrors } from "../../utils/common";
import { ButtonResetFilter, Iconify } from "../../components";

const KycSubmitted = () => {
  const [kycList, setKycList] = useState([]);
  const [fetchKycLoading, setFetchKycLoading] = useState(true);
  const [paginateTable, setPaginateTable] = useState({
    currentPage: 0,
    pageSize: 50,
  });
  const [filters, setFilters] = useState({
    search: "",
    kyc_review_status: "",
    party_type: "",
    status: "",
  });

  usePageTitle("KYC Submitted", "Review submitted KYC applications");

  const getKycList = useCallback(async () => {
    setFetchKycLoading(true);
    try {
      const params = {
        limit: paginateTable.pageSize,
        offset: paginateTable.currentPage * paginateTable.pageSize,
      };

      // Add filters to params if they have values
      if (filters.search) {
        params.search = filters.search;
      }
      if (filters.kyc_review_status) {
        params.kyc_review_status = filters.kyc_review_status;
      }
      if (filters.party_type) {
        params.party_type = filters.party_type;
      }
      if (filters.status) {
        params.status = filters.status;
      }

      const response = await merchantApi.get(API.KYC_ADMIN_LIST, { params });
      
      if (response.data && response.data.success && response.data.data) {
        // Transform response to match expected structure
        const transformedData = {
          count: response.data.data.total_count || 0,
          results: response.data.data.results || [],
        };
        setKycList(transformedData);
      } else {
        setKycList({ count: 0, results: [] });
      }
      setFetchKycLoading(false);
    } catch (error) {
      console.log(error);
      const errorMessages = handleApiErrors(error);
      errorMessages.forEach(({ field, message }) => {
        const formattedMessage = field ? `${field}: ${message}` : message;
        toast.error(formattedMessage);
      });
      setFetchKycLoading(false);
      setKycList({ count: 0, results: [] });
    }
  }, [paginateTable, filters]);

  useEffect(() => {
    const timer = setTimeout(() => {
      getKycList();
    }, 500);

    return () => clearTimeout(timer);
  }, [
    paginateTable.currentPage,
    paginateTable.pageSize,
    filters.search,
    filters.kyc_review_status,
    filters.party_type,
    filters.status,
    getKycList,
  ]);

  const handleSearch = (value) => {
    setFilters((prev) => ({ ...prev, search: value }));
    setPaginateTable((prev) => ({ ...prev, currentPage: 0 }));
  };

  const handleFilterChange = (filterName, value) => {
    setFilters((prev) => ({ ...prev, [filterName]: value }));
    setPaginateTable((prev) => ({ ...prev, currentPage: 0 }));
  };

  const handleClearFilters = () => {
    setFilters({
      search: "",
      kyc_review_status: "",
      party_type: "",
      status: "",
    });
    setPaginateTable((prev) => ({ ...prev, currentPage: 0 }));
  };

  const kycReviewStatusOptions = [
    { value: "pending_review", label: "Pending Review" },
    { value: "approved", label: "Approved" },
    { value: "rejected", label: "Rejected" },
  ];

  const partyTypeOptions = [
    { value: "B", label: "Business" },
    { value: "I", label: "Individual" },
  ];

  const statusOptions = [
    { value: "draft", label: "Draft" },
    { value: "active", label: "Active" },
  ];

  return (
    <Box sx={{ padding: "0 24px 24px" }}>
      <Stack
        sx={{
          overflowX: { xs: "auto" },
          overflowY: { xs: "visible", md: "unset" },
          padding: "5px 0",
        }}
        direction={"row"}
        justifyContent={"space-between"}
        gap={1}
        mt={2}
        width={"100%"}
        flexWrap={{ xs: "wrap", md: "nowrap" }}
      >
        <Stack
          sx={{ flexDirection: "row", gap: 1, flex: 1 }}
          divider={<Divider orientation="vertical" flexItem sx={{ borderStyle: "dashed" }} />}
        >
          <Stack sx={{ minWidth: "200px", flex: 1 }}>
            <TextField
              fullWidth
              size="small"
              value={filters.search}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder="Search"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Iconify icon="eva:search-fill" sx={{ color: "text.disabled" }} />
                  </InputAdornment>
                ),
              }}
            />
          </Stack>
          <Stack sx={{ minWidth: "200px", flex: 1 }}>
            <TextField
              select
              fullWidth
              size="small"
              label="KYC Review Status"
              value={filters.kyc_review_status}
              onChange={(e) => handleFilterChange("kyc_review_status", e.target.value)}
            >
              <MenuItem value="">--</MenuItem>
              {kycReviewStatusOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
          <Stack sx={{ minWidth: "200px", flex: 1 }}>
            <TextField
              select
              fullWidth
              size="small"
              label="Party Type"
              value={filters.party_type}
              onChange={(e) => handleFilterChange("party_type", e.target.value)}
            >
              <MenuItem value="">--</MenuItem>
              {partyTypeOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
          <Stack sx={{ minWidth: "200px", flex: 1 }}>
            <TextField
              select
              fullWidth
              size="small"
              label="Status"
              value={filters.status}
              onChange={(e) => handleFilterChange("status", e.target.value)}
            >
              <MenuItem value="">--</MenuItem>
              {statusOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </Stack>
        <Stack sx={{ flexDirection: "row", gap: 1, minWidth: "fit-content" }}>
          <ButtonResetFilter onClick={handleClearFilters} />
        </Stack>
      </Stack>
      <KycSubmittedTable
        fetchKycLoading={fetchKycLoading}
        kycList={kycList}
        paginateTable={paginateTable}
        setPaginateTable={setPaginateTable}
      />
    </Box>
  );
};

export default KycSubmitted;

